import { NgModule } from '@angular/core';
import { Route, RouterModule } from '@angular/router';
import { LoginComponent } from './account/login/login.component';
import { RegisterComponent } from './account/register/register.component';
import { VerifyComponent } from './account/verify/verify.component';
import { LayoutComponent } from './layout/layout.component';
import { DashboardComponent } from './account/dashboard/dashboard.component';
import { AuthGuard } from './services/auth.guard';
import { TenantListComponent } from './account/tenant-list/tenant-list.component';
import { TenantManagementComponent } from './settings/tenant-management/tenant-management.component';
import { TenantSignupsComponent } from './settings/tenant-signups/tenant-signups.component';
import { ForgetPasswordComponent } from './forget-password/forget-password.component';
import { SetPasswordComponent } from './account/set-password/set-password.component';
import { JoinTenantComponent } from './public-portal/join-tenant/join-tenant.component';
import { HomeComponent } from './public-portal/home/home.component';
import { PublicShellComponent } from './public-portal/public-shell/public-shell.component';
import { RootRedirectComponent } from './account/root-redirect/root-redirect.component';
import { rootRedirectGuard } from './services/root-redirect.guard';
import { tenantResolveGuard } from './services/tenant-resolve.guard';
import { adminRoleGuard } from './services/admin-role.guard';

const routes: Route[] = [
  // Root: on the public apex (no subdomain) go to the portal; on a tenant
  // subdomain go to login (or the dashboard when already signed in).
  { path: '', pathMatch: 'full', canActivate: [rootRedirectGuard], component: RootRedirectComponent },

  // Public portal shell (apex) — no authentication required. Wraps the
  // "Become a Tenant" onboarding and the public "Find a Teacher" search as tabs.
  {
    path: 'portal',
    component: PublicShellComponent,
    children: [
      { path: '', component: HomeComponent },
      { path: 'become-a-tenant', component: JoinTenantComponent },
      {
        path: 'find-teachers',
        loadChildren: () =>
          import('./public-portal/public-portal.module').then((m) => m.PublicPortalModule),
      },
    ],
  },

  // Legacy public routes -> portal tabs
  { path: 'find-teachers', redirectTo: 'portal/find-teachers', pathMatch: 'full' },
  { path: 'become-a-tenant', redirectTo: 'portal/become-a-tenant', pathMatch: 'full' },

  // Anonymous certificate verification (LMS L4) — the QR code target printed on certificates.
  {
    path: 'certificates/verify',
    loadChildren: () =>
      import('./certificates/certificate-verify.module').then((m) => m.CertificateVerifyModule),
  },

  // Anonymous trial-booking magic link from the booking emails: /booking/:id?token=
  { path: 'booking', loadChildren: () => import('./booking-link/booking-link.module').then((m) => m.BookingLinkModule) },

  // Super Admin console (full-screen dark shell; Administrator role only).
  {
    path: 'admin',
    canActivate: [adminRoleGuard],
    loadChildren: () =>
      import('./super-admin/super-admin.module').then((m) => m.SuperAdminModule),
  },

  { path: 'account/login', component: LoginComponent, canActivate: [tenantResolveGuard] },
  { path: 'account/register', component: RegisterComponent, canActivate: [tenantResolveGuard] },
  { path: 'account/verify', component: VerifyComponent },
  { path: 'account/forget-password', component: ForgetPasswordComponent, canActivate: [tenantResolveGuard] },
  { path: 'account/set-password', component: SetPasswordComponent, canActivate: [tenantResolveGuard] },

  // Main layout. canActivateChild (not canActivate) so AuthGuard runs on EVERY in-app navigation:
  // sign-in, page access, tenant modules, plan entitlements and role rules (page-access-state.service).
  {
    path: '',
    component: LayoutComponent,
    canActivateChild: [AuthGuard],
    children: [
      {
        path: 'teachers',
        loadChildren: () =>
          import('./teacher/teacher.module').then((m) => m.TeacherModule),
      },
      {
        path: 'student',
        loadChildren: () =>
          import('./student/student.module').then((m) => m.StudentModule),
      },
      {
        path: 'lectures',
        loadChildren: () =>
          import('./lectures/lecture.module').then((m) => m.LectureModule),
      },
      {
        path: 'lookup',
        loadChildren: () =>
          import('./Lookups/lookup.module').then((m) => m.LookUpModule),
      },
      {
        path: 'question-bank',
        loadChildren: () =>
          import('./question-bank/question-bank.module').then((m) => m.QuestionBankModule),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./settings/settings.module').then((m) => m.SettingsModule),
      },
      {
        path: 'teaching-assistant',
        loadChildren: () =>
          import('./teaching-assistant/teaching-assistant.module').then(
            (m) => m.TeachingAssistantModule
          ),
      },
      {
        path: 'teacher-panel',
        loadChildren: () =>
          import('./teacher-panel/teacher-panel.module').then(
            (m) => m.TeacherPanelModule
          ),
      },
      {
        path: 'quizzes',
        loadChildren: () =>
          import('./quizzes/quizzes.module').then((m) => m.QuizzesModule),
      },
      {
        path: 'classroom',
        loadChildren: () =>
          import('./classroom/classroom.module').then((m) => m.ClassroomModule),
      },
      {
        path: 'gradebook',
        loadChildren: () =>
          import('./gradebook/gradebook.module').then((m) => m.GradebookModule),
      },
      { path: 'school', loadChildren: () => import('./school/school.module').then((m) => m.SchoolModule) },
      {
        path: 'achievements',
        loadChildren: () =>
          import('./gamification/gamification.module').then((m) => m.GamificationModule),
      },
      {
        path: 'calendar',
        loadChildren: () =>
          import('./calendar/calendar.module').then((m) => m.CalendarModule),
      },

      {
        path: 'messages',
        loadChildren: () =>
          import('./messages/messages.module').then((m) => m.MessagesModule),
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('./notifications/notifications.module').then((m) => m.NotificationsModule),
      },
      {
        path: 'parent',
        loadChildren: () =>
          import('./parent/parent.module').then((m) => m.ParentModule),
      },
      {
        path: 'courses',
        loadChildren: () =>
          import('./courses/courses.module').then((m) => m.CoursesModule),
      },
      {
        path: 'assessments',
        loadChildren: () =>
          import('./assessments/assessments.module').then((m) => m.AssessmentsModule),
      },
      {
        path: 'lessons',
        loadChildren: () =>
          import('./lessons/lessons.module').then((m) => m.LessonsModule),
      },
      {
        path: 'lesson-presentations',
        loadChildren: () =>
          import('./lesson-presentations/lesson-presentations.module').then(
            (m) => m.LessonPresentationsModule
          ),
      },
      { path: 'academy', loadChildren: () => import('./academy/academy.module').then((m) => m.AcademyModule) },
      {
        path: 'ai',
        loadChildren: () => import('./ai/ai.module').then((m) => m.AiModule),
      },

      // Protected account routes
      {
        path: 'account/dashboard',
        component: DashboardComponent,
      },
      {
        path: 'account/tenant-list',
        component: TenantListComponent,
      },
      {
        path: 'account/tenant-management',
        component: TenantManagementComponent,
      },
      {
        path: 'account/tenant-signups',
        component: TenantSignupsComponent,
      },
    ],
  },

  // Catch-all fallback — re-run the root guard so apex vs tenant is honored.
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Reset scroll to the top on every navigation (the sidebar is long, so without
      // this you land mid-page and have to scroll up).
      scrollPositionRestoration: 'top',
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
